import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createSMTPConfig, updateSMTPConfig, testSMTPConnection, validateSMTPConfig, testSMTPConnectionWithConfig } from '../../services/smtpService';
import { SMTPConfig, SMTPConfigForm as SMTPConfigFormData, SMTP_PROVIDER_TEMPLATES } from '../../types/admin';
import './SMTPConfigForm.css';

interface SMTPConfigFormProps {
	config?: SMTPConfig | null;
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	mode: 'create' | 'edit';
}

export const SMTPConfigForm: React.FC<SMTPConfigFormProps> = ({ config, isOpen, onClose, onSuccess, mode }) => {
	const { token } = useAuth();
	const [loading, setLoading] = useState(false);
	const [testingConnection, setTestingConnection] = useState(false);
	const [error, setError] = useState<string>('');
	const [connectionTestResult, setConnectionTestResult] = useState<{
		success: boolean;
		message: string;
	} | null>(null);

	const [formData, setFormData] = useState<SMTPConfigFormData>({
		name: '',
		host: '',
		port: 587,
		username: '',
		password: '',
		from_email: '',
		from_name: '',
		secure: false,
		require_tls: true,
		is_active: true,
		is_default: false,
	});

	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<string>('');

	useEffect(() => {
		if (mode === 'edit' && config) {
			setFormData({
				name: config.name,
				host: config.host,
				port: config.port,
				username: config.username,
				password: '', // Never pre-fill password for security
				from_email: config.from_email,
				from_name: config.from_name || '',
				secure: config.secure,
				require_tls: config.require_tls,
				is_active: config.is_active,
				is_default: config.is_default,
			});
		} else {
			setFormData({
				name: '',
				host: '',
				port: 587,
				username: '',
				password: '',
				from_email: '',
				from_name: '',
				secure: false,
				require_tls: true,
				is_active: true,
				is_default: false,
			});
		}
		setValidationErrors({});
		setError('');
		setConnectionTestResult(null);
		setSelectedTemplate('');
	}, [mode, config, isOpen]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value, type } = e.target;
		const checked = (e.target as HTMLInputElement).checked;

		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
		}));

		// Clear validation error when user starts typing
		if (validationErrors[name]) {
			setValidationErrors((prev) => ({ ...prev, [name]: '' }));
		}
	};

	const handleTemplateChange = (templateName: string) => {
		setSelectedTemplate(templateName);

		if (templateName && templateName !== 'custom') {
			const template = SMTP_PROVIDER_TEMPLATES.find((t) => t.name === templateName);
			if (template) {
				setFormData((prev) => ({
					...prev,
					name: prev.name || template.displayName,
					host: template.host,
					port: template.port,
					secure: template.secure,
					require_tls: template.require_tls,
				}));
			}
		}
	};

	const validateForm = () => {
		const errors = validateSMTPConfig(formData);
		const errorMap: Record<string, string> = {};

		errors.forEach((error) => {
			if (error.includes('name')) errorMap.name = error;
			else if (error.includes('host')) errorMap.host = error;
			else if (error.includes('Port')) errorMap.port = error;
			else if (error.includes('Username')) errorMap.username = error;
			else if (error.includes('Password')) errorMap.password = error;
			else if (error.includes('email')) errorMap.from_email = error;
		});

		setValidationErrors(errorMap);
		return Object.keys(errorMap).length === 0;
	};

	const handleTestConnection = async () => {
		if (!validateForm()) {
			setError('Molimo popravite greške validacije pre testiranja konekcije');
			return;
		}

		setTestingConnection(true);
		setConnectionTestResult(null);
		setError('');

		try {
			// const result = await testSMTPConnectionWithConfig(token!, formData);
			const result = await testSMTPConnection(token!);
			setConnectionTestResult({
				success: result.success,
				message: result.success ? 'Konekcija uspešna!' : result.error || 'Konekcija neuspešna',
			});
		} catch (err) {
			setConnectionTestResult({
				success: false,
				message: err instanceof Error ? err.message : 'Test konekcije neuspešan',
			});
		} finally {
			setTestingConnection(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			setError('Molimo popravite greške validacije');
			return;
		}

		setLoading(true);
		setError('');

		try {
			if (mode === 'create') {
				await createSMTPConfig(token!, formData);
			} else if (config) {
				// For edit mode, only send password if it's been changed
				const updateData: any = { ...formData };
				if (!updateData.password) {
					delete updateData.password;
				}
				await updateSMTPConfig(token!, config.id, updateData);
			}

			onSuccess();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Operacija neuspešna');
		} finally {
			setLoading(false);
		}
	};

	if (!isOpen) return null;

	const selectedTemplateData = selectedTemplate && selectedTemplate !== 'custom' ? SMTP_PROVIDER_TEMPLATES.find((t) => t.name === selectedTemplate) : null;

	return (
		<div className='modal-overlay'>
			<div className='modal-content smtp-form-modal'>
				<div className='modal-header'>
					<h3>{mode === 'create' ? 'Dodaj SMTP konfiguraciju' : 'Izmeni SMTP konfiguraciju'}</h3>
					<button className='close-btn' onClick={onClose}>
						&times;
					</button>
				</div>

				<form onSubmit={handleSubmit} className='smtp-form'>
					{error && <div className='error-message'>{error}</div>}

					{/* Provider Template Selection */}
					{/* <div className='form-section'>
						<h4>Dobavljač e-pošte</h4>
						<div className='form-group'>
							<label htmlFor='template'>Izaberite šablon dobavljača (opciono)</label>
							<select id='template' value={selectedTemplate} onChange={(e) => handleTemplateChange(e.target.value)} className='form-control'>
								<option value=''>Prilagođena konfiguracija</option>
								<option value='custom'>Prilagođena konfiguracija</option>
								{SMTP_PROVIDER_TEMPLATES.map((template) => (
									<option key={template.name} value={template.name}>
										{template.displayName}
									</option>
								))}
							</select>
						</div>

						{selectedTemplateData && (
							<div className='template-instructions'>
								<div className='alert alert-info'>
									<strong>Uputstva za {selectedTemplateData.displayName}:</strong>
									<p>{selectedTemplateData.instructions}</p>
								</div>
							</div>
						)}
					</div> */}

					{/* Basic Configuration */}
					<div className='form-section'>
						<h4>Osnovna konfiguracija</h4>
						<div className='form-row'>
							<div className='form-group'>
								<label htmlFor='name'>Naziv konfiguracije *</label>
								<input type='text' id='name' name='name' value={formData.name} onChange={handleInputChange} className={`form-control ${validationErrors.name ? 'error' : ''}`} placeholder='npr. Gmail SMTP' required />
								{validationErrors.name && <span className='error-text'>{validationErrors.name}</span>}
							</div>
						</div>

						<div className='form-row'>
							<div className='form-group'>
								<label htmlFor='host'>SMTP host *</label>
								<input type='text' id='host' name='host' value={formData.host} onChange={handleInputChange} className={`form-control ${validationErrors.host ? 'error' : ''}`} placeholder='smtp.gmail.com' required />
								{validationErrors.host && <span className='error-text'>{validationErrors.host}</span>}
							</div>
							<div className='form-group'>
								<label htmlFor='port'>Port *</label>
								<input type='number' id='port' name='port' value={formData.port} onChange={handleInputChange} className={`form-control ${validationErrors.port ? 'error' : ''}`} min='1' max='65535' required />
								{validationErrors.port && <span className='error-text'>{validationErrors.port}</span>}
							</div>
						</div>

						<div className='form-row'>
							<div className='form-group'>
								<label htmlFor='username'>{selectedTemplateData?.usernameLabel || 'Korisničko ime'} *</label>
								<input type='text' id='username' name='username' value={formData.username} onChange={handleInputChange} className={`form-control ${validationErrors.username ? 'error' : ''}`} placeholder='vas-email@example.com' required />
								{validationErrors.username && <span className='error-text'>{validationErrors.username}</span>}
							</div>
							<div className='form-group'>
								<label htmlFor='password'>{selectedTemplateData?.passwordLabel || 'Lozinka'} *</label>
								<input type='password' id='password' name='password' value={formData.password} onChange={handleInputChange} className={`form-control ${validationErrors.password ? 'error' : ''}`} placeholder={mode === 'edit' ? 'Ostavite prazno da zadržite trenutnu lozinku' : 'Unesite lozinku'} required={mode === 'create'} />
								{validationErrors.password && <span className='error-text'>{validationErrors.password}</span>}
							</div>
						</div>

						<div className='form-row'>
							<div className='form-group'>
								<label htmlFor='from_email'>Email pošaljioca *</label>
								<input type='email' id='from_email' name='from_email' value={formData.from_email} onChange={handleInputChange} className={`form-control ${validationErrors.from_email ? 'error' : ''}`} placeholder='noreply@vasadomena.com' required />
								{validationErrors.from_email && <span className='error-text'>{validationErrors.from_email}</span>}
							</div>
							<div className='form-group'>
								<label htmlFor='from_name'>Ime pošaljioca</label>
								<input type='text' id='from_name' name='from_name' value={formData.from_name} onChange={handleInputChange} className='form-control' placeholder='Sistem protiv trgovine ljudima' />
							</div>
						</div>
					</div>

					{/* Advanced Settings */}
					<div className='form-section'>
						<button type='button' className='toggle-advanced-btn' onClick={() => setShowAdvanced(!showAdvanced)}>
							Napredne postavke {showAdvanced ? '▼' : '▶'}
						</button>

						{showAdvanced && (
							<div className='advanced-settings'>
								<div className='form-row'>
									<div className='form-group checkbox-group'>
										<label className='checkbox-label'>
											<input type='checkbox' name='secure' checked={formData.secure} onChange={handleInputChange} />
											<span className='checkmark'></span>
											Koristi SSL/TLS (port 465)
										</label>
									</div>
									<div className='form-group checkbox-group'>
										<label className='checkbox-label'>
											<input type='checkbox' name='require_tls' checked={formData.require_tls} onChange={handleInputChange} />
											<span className='checkmark'></span>
											Zahteva TLS
										</label>
									</div>
								</div>

								<div className='form-row'>
									<div className='form-group checkbox-group'>
										<label className='checkbox-label'>
											<input type='checkbox' name='is_active' checked={formData.is_active} onChange={handleInputChange} />
											<span className='checkmark'></span>
											Aktivna konfiguracija
										</label>
									</div>
									<div className='form-group checkbox-group'>
										<label className='checkbox-label'>
											<input type='checkbox' name='is_default' checked={formData.is_default} onChange={handleInputChange} />
											<span className='checkmark'></span>
											Postavi kao podrazumevano
										</label>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Connection Test */}
					<div className='form-section'>
						<div className='test-connection-section'>
							<button type='button' onClick={handleTestConnection} disabled={testingConnection} className='test-connection-btn'>
								{testingConnection ? 'Testiranje...' : 'Testiraj konekciju'}
							</button>

							{connectionTestResult && (
								<div className={`connection-result ${connectionTestResult.success ? 'success' : 'error'}`}>
									<span className={`status-icon ${connectionTestResult.success ? 'success' : 'error'}`}>{connectionTestResult.success ? '✓' : '✗'}</span>
									{connectionTestResult.message}
								</div>
							)}
						</div>
					</div>

					{/* Form Actions */}
					<div className='form-actions'>
						<button type='button' onClick={onClose} className='btn btn-secondary' disabled={loading}>
							Odustani
						</button>
						<button type='submit' className='btn btn-primary' disabled={loading}>
							{loading ? 'Čuvam...' : mode === 'create' ? 'Napravi konfiguraciju' : 'Ažuriraj konfiguraciju'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
